import clsx from 'clsx';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { SECTION } from '../primitives/shell.tsx';

export type Tone = 'good' | 'warn' | 'off';

/** What a section's index entry says of it at a glance: a dot, and words for whoever cannot see it. */
export interface Status {
  tone: Tone;
  label: string;
}

interface Entry {
  id: string;
  title: string;
  status: Status | null;
}

/** Warn pulses: it is the one that asks something of the reader. */
const DOT: Record<Tone, string> = {
  good: 'bg-ink',
  warn: 'bg-warn animate-pulse',
  off: 'bg-ink-faint',
};

export function StatusDot({ tone, className }: { tone: Tone; className?: string }) {
  return (
    <span aria-hidden className={clsx('size-1.5 shrink-0 rounded-full', DOT[tone], className)} />
  );
}

type Register = (entry: Entry, gone?: boolean) => void;

const Registry = createContext<Register | null>(null);

/** In the order the page draws them, whichever registered first. */
function byPlace(a: Entry, b: Entry): number {
  const here = document.getElementById(a.id);
  const there = document.getElementById(b.id);
  if (!here || !there) return 0;
  return here.compareDocumentPosition(there) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

/** How far below the app's header a section's top must pass to be the one being read. */
const READING_LINE_PX = 120;

/**
 * The last section whose top has passed the reading line, or the last of all once the page can
 * scroll no further: a short final section never reaches the line.
 */
function useReading(ids: readonly string[]): string | null {
  const [here, setHere] = useState<string | null>(null);
  const key = ids.join(' ');

  useEffect(() => {
    const order = key === '' ? [] : key.split(' ');
    let frame = 0;
    const look = () => {
      frame = 0;
      const head = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--app-head') || '0',
      );
      const line = head + READING_LINE_PX;
      const atEnd =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let reading = order[0] ?? null;
      for (const id of order) {
        const top = document.getElementById(id)?.getBoundingClientRect().top;
        if (top !== undefined && top <= line) reading = id;
      }
      setHere(atEnd && window.scrollY > 0 ? (order.at(-1) ?? reading) : reading);
    };
    const soon = () => {
      if (frame === 0) frame = requestAnimationFrame(look);
    };
    look();
    window.addEventListener('scroll', soon, { passive: true });
    window.addEventListener('resize', soon);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', soon);
      window.removeEventListener('resize', soon);
    };
  }, [key]);

  return here;
}

/**
 * Sections with an index of them: beside them on a wide screen and following the reading, a row
 * of chips above them on a narrow one. The index lists only the sections actually drawn.
 */
export function SectionedPage({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const register = useCallback<Register>((entry, gone = false) => {
    setEntries((was) => {
      const rest = was.filter((one) => one.id !== entry.id);
      return gone ? rest : [...rest, entry].sort(byPlace);
    });
  }, []);
  const here = useReading(entries.map((entry) => entry.id));

  return (
    <Registry.Provider value={register}>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[9.5rem_minmax(0,1fr)] lg:items-start lg:gap-10">
        <nav
          aria-label="Sections"
          className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1 lg:sticky lg:top-[calc(var(--app-head)+1.5rem)] lg:mx-0 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:px-0"
        >
          {entries.map((entry) => {
            const on = entry.id === here;
            return (
              <a
                key={entry.id}
                href={`#${entry.id}`}
                aria-current={on ? 'location' : undefined}
                className={clsx(
                  'flex shrink-0 items-center justify-between gap-2 rounded-control px-2.5 py-1.5 text-xs whitespace-nowrap transition-colors max-lg:border touch:py-2',
                  on
                    ? 'bg-surface-raised font-medium text-ink max-lg:border-line'
                    : 'text-ink-muted hover:text-ink max-lg:border-line-soft',
                )}
              >
                {entry.title}
                {entry.status && (
                  <span className="flex items-center" title={entry.status.label}>
                    <StatusDot tone={entry.status.tone} />
                    <span className="sr-only">{entry.status.label}</span>
                  </span>
                )}
              </a>
            );
          })}
        </nav>
        <div className="flex min-w-0 flex-col gap-10">{children}</div>
      </div>
    </Registry.Provider>
  );
}

/** One section of a `SectionedPage`: its heading and line outside the card, so every section reads alike. */
export function PageSection({
  id,
  title,
  blurb,
  status,
  aside,
  children,
}: {
  id: string;
  title: string;
  blurb?: ReactNode;
  status?: Status | null;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const register = useContext(Registry);
  const tone = status?.tone;
  const label = status?.label;
  useEffect(() => {
    if (!register) return;
    const entry = { id, title, status: tone && label ? { tone, label } : null };
    register(entry);
    return () => register(entry, true);
  }, [register, id, title, tone, label]);

  return (
    <section id={id} aria-labelledby={`${id}-title`} className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={`${id}-title`} className={SECTION}>
            {title}
          </h2>
          {blurb && <p className="max-w-prose text-xs leading-relaxed text-ink-muted">{blurb}</p>}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}

/** A card of settings, one to a row, ruled apart. */
export function Rows({ tone, children }: { tone?: 'danger'; children: ReactNode }) {
  return (
    <div
      className={clsx(
        'flex flex-col divide-y rounded-box border',
        tone === 'danger'
          ? 'divide-down/20 border-down/40 bg-down/5'
          : 'divide-line-soft border-line bg-surface',
      )}
    >
      {children}
    </div>
  );
}

/** What the setting is and what it does on the left, its control on the right; anything it opens below. */
export function Row({
  title,
  hint,
  control,
  children,
}: {
  title: ReactNode;
  hint?: ReactNode;
  control?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="p-card flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-1 basis-64 flex-col gap-0.5">
          <span className="text-sm font-medium text-ink">{title}</span>
          {hint && <span className="text-xs leading-relaxed text-ink-muted">{hint}</span>}
        </div>
        {control && <div className="flex shrink-0 items-center gap-2">{control}</div>}
      </div>
      {children}
    </div>
  );
}
